import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import DynamicIconWrapper from '@components/common/DynamicIconWrapper';
import FlatTreeXMLChevron from '@components/common/FlatXMLTree/FlatTreeXMLWrapper/FlatTreeXMLChevron';

import { Icons } from '@icons/icons';

import colors from '@constants/colors';
import { HALF_SECOND } from '@constants/common';

import { IFlatXMLTreeError, IFlatXMLTreeNode } from '../../interface';
import { getHighlightedText, getXMLTags } from '../../utils';
import SkipFlatNodeButton from '../SkipFlatNodeButton';

import styles from './FlatTreeXMLNode.module.scss';

interface IFlatTreeXMLNodeProps {
	node: IFlatXMLTreeNode;
	toggleCollapse: () => void;
	enterEditMode: () => void;
	skipNodeHandler: () => void;
	enterAddMode?: () => void;
	onRevert: () => void;
	onRemove: () => void;
	onCopy: () => void;
	isCollapsed: boolean;
	isReadOnly?: boolean;
	isWithSkip?: boolean;
	isWithCopy?: boolean;
	isWithAdd?: boolean;
	searchValue?: string;
	error?: IFlatXMLTreeError;
}

const iconStyle = { height: '16px', width: '16px' };
const wrapperStyle = { position: 'relative', bottom: '2px' };

const FlatTreeXMLNode = ({
	node,
	isCollapsed,
	isReadOnly,
	isWithSkip,
	isWithCopy,
	isWithAdd,
	searchValue,
	error,
	toggleCollapse,
	enterEditMode,
	enterAddMode,
	skipNodeHandler,
	onRevert,
	onRemove,
	onCopy,
}: IFlatTreeXMLNodeProps) => {
	const [isCopied, setIsCopied] = useState(false);
	const timerId = useRef<NodeJS.Timeout>();

	const nodeContentClasses = cn(styles.nodeContent, {
		[styles.readOnlyPadding]: !node.nodeType && !isWithSkip,
		[styles.error]: !!error,
	});

	const { closedTag, openedTag } = getXMLTags(node.key);

	const isChangedValue = node.value !== node.defaultValue || node.isObjectAdded;
	const isParameterNodeEditable = !node.nodeType && !isReadOnly && !node.isNonEditable;
	const isSkipAllowed = isParameterNodeEditable && isWithSkip;
	const isDeleteAllowed = (!node.nodeType || node.isObjectAdded) && isWithSkip && !node.isNonEditable;

	const isSearchMatch = useMemo(() => {
		return searchValue && (
			node.value?.toLowerCase().trim().includes(searchValue.toLowerCase().trim()) ||
			node.key?.toLowerCase().trim().includes(searchValue.toLowerCase().trim())
		);
	}, [searchValue, node.value, node.key]);

	const isClosedTagVisible = node.isHiddenChildren && node.nodeType;
	const description = node.description;

	const keyClass = error
		? styles.error
		: isChangedValue
			? styles.nodeChanged
			: styles.nodeKey;
	const valueClass = isChangedValue ? styles.nodeChanged : styles.nodeValue;

	const copyLabelClass = cn(styles.copyLabel, {
		[styles.copyLabelVisible]: isCopied,
	});

	useEffect(() => {
		if (isCopied && !timerId.current) {
			timerId.current = setTimeout(() => {
				setIsCopied(false);
			}, HALF_SECOND);
		}

		return () => {
			clearTimeout(timerId.current);
			timerId.current = undefined;
		};
	}, [isCopied]);

	const copyHandler = () => {
		setIsCopied(true);
		onCopy();
	};

	return (
		<div
			className={styles.node}
			data-testid={`node_${node.path}`}
		>
			{isSkipAllowed &&
				<SkipFlatNodeButton
					isSkipped={!node.isChecked}
					onClick={skipNodeHandler}
				/>
			}
			{node.nodeType &&
				<FlatTreeXMLChevron
					isCollapsed={isCollapsed}
					testName={node.path}
					toggleCollapse={toggleCollapse}
				/>
			}
			<div className={nodeContentClasses}>
					<span
						className={keyClass}
						data-testid='node_key'
					>
						{isSearchMatch ? getHighlightedText(openedTag, styles.searchValue, searchValue) : openedTag}
					</span>
				{node.value !== undefined &&
					<span
						className={valueClass}
						data-testid='node_value'
					>
						<span>{
							isSearchMatch
								? getHighlightedText(node.value, styles.searchValue, searchValue)
								: node.value
						}</span>
						<span className={keyClass}>{closedTag}</span>
					</span>
				}
				{isClosedTagVisible && <span className={keyClass}>...{closedTag}</span>}
				{description &&
					<DynamicIconWrapper
						icon={Icons.info}
						iconStyle={iconStyle}
						tooltipLabel={description}
						wrapperStyle={wrapperStyle}
					/>
				}
				{node.nodeType && !isReadOnly && isWithAdd && (
					<DynamicIconWrapper
						icon={Icons.plus}
						iconStyle={iconStyle}
						onClick={enterAddMode}
						testName={`_${node.path}_add`}
						tooltipLabel='Add parameter'
						wrapperStyle={wrapperStyle}
					/>
				)}
				{isParameterNodeEditable && (
					<DynamicIconWrapper
						icon={Icons.editIcon}
						iconStyle={iconStyle}
						onClick={enterEditMode}
						testName={`_${node.path}_edit`}
						tooltipLabel='Edit parameter'
						wrapperStyle={wrapperStyle}
					/>
				)}
				{isWithCopy && (
					<span className={styles.copyWrapper}>
						<DynamicIconWrapper
							icon={Icons.copy}
							iconStyle={iconStyle}
							onClick={copyHandler}
							testName={`_${node.path}_copy`}
							tooltipLabel='Copy parameter'
						/>
						<span className={copyLabelClass}>copied</span>
					</span>
				)}
				{isDeleteAllowed && (
					<DynamicIconWrapper
						icon={Icons.delete}
						iconStyle={iconStyle}
						onClick={onRemove}
						testName={`_${node.path}_delete`}
						tooltipLabel='Delete parameter'
						wrapperStyle={wrapperStyle}
					/>
				)}
				{isChangedValue && !isReadOnly && node.defaultValue !== undefined && (
					<DynamicIconWrapper
						icon={Icons.revert}
						iconStyle={iconStyle}
						onClick={onRevert}
						testName={`_${node.path}_revert`}
						tooltipLabel='Revert changes'
						wrapperStyle={wrapperStyle}
					/>
				)}
				{error &&
					<DynamicIconWrapper
						icon={Icons.errorPlateIcon}
						iconStyle={{ color: colors.red50, fill: colors.white, width: '16px', height: '16px' }}
						tooltipLabel={error.errorMessage}
						tooltipPosition='right'
						wrapperStyle={wrapperStyle}
					/>
				}
			</div>
		</div>
	);
};

export default memo(FlatTreeXMLNode);
